import { X } from 'lucide-react';
import defaultStops from '../../data/linqOne/map_route.json';
import { darkTheme, lightTheme } from '../../theme/theme';

const RouteOverlay = ({ 
    stops = defaultStops, 
    isDark = true, 
    onViewFull,
    onViewGraph,
    className = "",
    showTemperature = false
}) => {
    const theme = isDark ? darkTheme : lightTheme;
    const previewStops = stops.slice(-2);

    // Temperature coloring logic
    const temps = stops.map(s => parseFloat(s.temperature)).filter(n => !isNaN(n));
    const maxTemp = temps.length ? Math.max(...temps) : -Infinity;
    const minTemp = temps.length ? Math.min(...temps) : Infinity;

    const getStopColor = (stop, index) => {
        if (!showTemperature) return index === 0 ? '#FF4B7E' : (index === 1 ? '#FFB075' : '#2AB572');
        
        const t = parseFloat(stop.temperature);
        if (isNaN(t)) return '#3B82F6'; // Default Blue
        if (t === maxTemp) return '#EF4444'; // Red
        if (t === minTemp) return '#22C55E'; // Green
        return '#3B82F6'; // Blue
    };

    return (
        <div 
            className={`rounded-2xl p-4 w-full shadow-lg ${className}`}
            style={{ 
                backgroundColor: theme.background2, 
                color: theme.text,
            }}
        >
            <h3 className="font-semibold mb-4" style={{ color: theme.text }}>Route Stops</h3>
            
            <div className="space-y-4 mb-4">
                {previewStops.map((stop, index) => (
                    <div key={stop.id} className="flex items-center gap-3">
                        <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm"
                            style={{ 
                                backgroundColor: getStopColor(stop, index),
                                color: 'white',
                                lineHeight: '1'
                             }}
                        >
                            {stop.label}
                        </div>
                        <div className="flex flex-col flex-1">
                            {showTemperature ? (
                                <>
                                    <span className="font-medium text-sm">{stop.title} {stop.subtitle && `- ${stop.subtitle}`}</span>
                                    {stop.temperature && (
                                        <span className="text-xs text-gray-400">{stop.temperature}</span>
                                    )}
                                </>
                            ) : (
                                <>
                                    {index === previewStops.length - 1 && (
                                        <span className="text-xs text-gray-400">Last Stop</span>
                                    )}
                                    <span className="font-medium">{stop.title}</span>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex gap-3">
                <button
                    onClick={onViewFull}
                    className={`${showTemperature ? 'flex-1' : 'w-full'} py-3 rounded-xl font-medium text-sm transition-colors`}
                    style={{ 
                        backgroundColor: theme.button, 
                        color: 'white' 
                    }}
                >
                    View Full Details
                </button>
                
                {showTemperature && (
                    <button
                        onClick={onViewGraph}
                        className="flex-1 py-3 rounded-xl font-medium text-sm transition-colors border"
                        style={{ 
                            borderColor: theme.button, 
                            color: theme.text,
                            backgroundColor: 'transparent'
                        }}
                    >
                        View Graph
                    </button>
                )}
            </div>
        </div>
    );
};

const RouteFullModal = ({ 
    stops = defaultStops, 
    isOpen, 
    onClose, 
    isDark = true,
    showTemperature = false
}) => {
    const theme = isDark ? darkTheme : lightTheme;
    const totalStops = stops.length;
    const reversedStops = [...stops].reverse();

    // Temperature coloring logic
    const temps = stops.map(s => parseFloat(s.temperature)).filter(n => !isNaN(n));
    const maxTemp = temps.length ? Math.max(...temps) : -Infinity;
    const minTemp = temps.length ? Math.min(...temps) : Infinity;

    const getStopColor = (stop, index) => {
        if (!showTemperature) return index === 0 ? theme.greenbg : theme.gray;

        const t = parseFloat(stop.temperature);
        if (isNaN(t)) return '#3B82F6'; // Default Blue
        if (t === maxTemp) return '#EF4444'; // Red
        if (t === minTemp) return '#22C55E'; // Green
        return '#3B82F6'; // Blue
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 z-50 flex flex-col p-4 animate-in slide-in-from-bottom-10"
            style={{ backgroundColor: theme.background1 }}
        >
            <div className="flex justify-between items-center mb-6 pt-safe">
                <h2 className="text-xl mt-4 font-bold" style={{ color: theme.text }}>Route Stops</h2>
                <button onClick={onClose} className="p-2">
                    <X size={24} style={{ color: theme.text }} />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4">
                {reversedStops.map((stop, index) => (
                    <div 
                        key={stop.id}
                        className="p-4 rounded-2xl flex items-center gap-4"
                        style={{ backgroundColor: theme.background2,border: `1px solid ${theme.stroke}` }}
                    >
                        <div 
                            className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0"
                            style={{ 
                                backgroundColor: (index === 0 && !showTemperature) ? theme.border : theme.stroke,
                             }}
                        >
                            <div 
                            className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-lg flex-shrink-0"
                            style={{ 
                                backgroundColor: getStopColor(stop, index),
                                color: 'white',
                                lineHeight: '1'
                             }}
                        >
                           {stop.label}
                        </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                            {showTemperature ? (
                                <>
                                    <h3 className="font-semibold truncate" style={{ color: theme.text }}>
                                        {stop.title} {stop.subtitle && `(${stop.subtitle})`}
                                    </h3>
                                    {stop.temperature && (
                                        <span className="text-xs font-medium" style={{ color: theme.text }}>
                                            {stop.temperature}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-400 mb-1 block">Stop {totalStops - index}</span>
                                        
                                    </div>
                                    <h3 className="font-semibold truncate" style={{ color: theme.text }}>
                                        {stop.title} ({stop.subtitle})
                                    </h3>
                                </>
                            )}
                        </div>

                        {index === 0 && (
                            <div 
                                className="px-3 py-1 rounded-full text-xs font-medium"
                                style={{ backgroundColor: '#A2E9F7', color: '#005C7A' }}
                            >
                                Current
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export { RouteFullModal, RouteOverlay };
